import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Skeleton, Snackbar,
  Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsPencil, BsPlusLg, BsTrash } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Fiche from '../Expert/Fiche';
import { themeExpert } from '../../theme/expert';

const CONTEXTES = [
  { code: 'expertise', libelle: 'Dossier' },
  { code: 'document', libelle: 'Document' },
  { code: 'both', libelle: 'Les deux' },
];

const VIDE = {
  name: '', prompt: '', context_type: 'expertise', position: 0,
  allowed_tools: [], is_active: true,
};

const contexte = (code) =>
  CONTEXTES.find((c) => c.code === code)?.libelle || code;

/** Les questions toutes prêtes que l'assistant propose en un bouton. */
function AssistantSkills() {
  const [recettes, setRecettes] = useState([]);
  const [outils, setOutils] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  const [fiche, setFiche] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/ged/assistant-skills/');
      setRecettes(Array.isArray(data) ? data : []);
      setErreur('');
    } catch {
      setErreur("Les recettes n'ont pas pu être chargées.");
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
    axiosInstance.get('/ged/agent-tools/')
      .then(({ data }) => setOutils(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [charger]);

  const poser = (champ) => (e) => setFiche((etat) => ({
    ...etat,
    [champ]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const basculerOutil = (nom) => setFiche((etat) => ({
    ...etat,
    allowed_tools: etat.allowed_tools.includes(nom)
      ? etat.allowed_tools.filter((o) => o !== nom)
      : [...etat.allowed_tools, nom],
  }));

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = { ...fiche, position: parseInt(fiche.position, 10) || 0 };
      if (fiche.id) {
        await axiosInstance.put(`/ged/assistant-skills/${fiche.id}/`, corps);
      } else {
        await axiosInstance.post('/ged/assistant-skills/', corps);
      }
      setFiche(null);
      setMessage('Recette enregistrée');
      charger();
    } catch {
      setErreur("La recette n'a pas pu être enregistrée.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/ged/assistant-skills/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Recette supprimée');
      charger();
    } catch {
      setErreur("La recette n'a pas pu être supprimée.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <>
      <Fiche
        titre="Recettes de l'assistant"
        explication="Elles apparaissent en boutons dans le chat, selon qu'on est sur un dossier ou sur un document."
        actions={(
          <Button size="small" variant="contained" startIcon={<BsPlusLg />}
                  onClick={() => setFiche({ ...VIDE })}>
            Nouvelle recette
          </Button>
        )}
        sx={{ p: 0 }}
      >
        {erreur && <Alert severity="error" sx={{ m: 2 }}
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        {chargement && (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} height={36} />)}
          </Stack>
        )}

        {!chargement && recettes.length === 0 && (
          <Typography variant="body2" color="text.secondary"
                      sx={{ py: 4, textAlign: 'center' }}>
            Aucune recette. L'assistant proposera ses départs par défaut.
          </Typography>
        )}

        {!chargement && recettes.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Libellé du bouton</TableCell>
                  <TableCell>Contexte</TableCell>
                  <TableCell align="right">Ordre</TableCell>
                  <TableCell>Outils autorisés</TableCell>
                  <TableCell align="right">&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recettes.map((recette) => (
                  <TableRow key={recette.id} hover
                            sx={{ opacity: recette.is_active === false ? 0.5 : 1 }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center"
                             flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {recette.name}
                        </Typography>
                        {recette.is_active === false && (
                          <Chip size="small" label="inactive" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{contexte(recette.context_type)}</TableCell>
                    <TableCell align="right">{recette.position}</TableCell>
                    <TableCell>
                      {recette.allowed_tools?.length > 0
                        ? recette.allowed_tools.join(', ')
                        : <Box component="span"
                               sx={{ color: 'text.disabled' }}>tous</Box>}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Modifier">
                        <IconButton size="small"
                                    onClick={() => setFiche({
                                      ...VIDE, ...recette,
                                      allowed_tools: recette.allowed_tools || [],
                                      is_active: recette.is_active !== false })}>
                          <BsPencil />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small"
                                    onClick={() => setASupprimer(recette)}>
                          <BsTrash />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Fiche>

      <ThemeProvider theme={themeExpert}>
        <Dialog open={fiche !== null} onClose={() => setFiche(null)}
                fullWidth maxWidth="md">
          <Box component="form" onSubmit={enregistrer} noValidate>
            <DialogTitle>
              {fiche?.id ? 'Modifier la recette' : 'Nouvelle recette'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <TextField label="Libellé du bouton" value={fiche?.name || ''}
                           onChange={poser('name')} required autoFocus
                           helperText="Court : c'est ce que l'expert lira sur le bouton." />

                <TextField label="Question envoyée à l'IA" multiline minRows={4}
                           value={fiche?.prompt || ''}
                           onChange={poser('prompt')} required
                           helperText="Écrite comme si vous la posiez vous-même." />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                       alignItems={{ sm: 'center' }}>
                  <TextField select label="Contexte"
                             value={fiche?.context_type || 'expertise'}
                             onChange={poser('context_type')}
                             sx={{ minWidth: 180 }}>
                    {CONTEXTES.map(({ code, libelle }) => (
                      <MenuItem key={code} value={code}>{libelle}</MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Ordre" type="number"
                             value={fiche?.position ?? 0}
                             onChange={poser('position')}
                             sx={{ maxWidth: { sm: 120 } }} />
                  <FormControlLabel
                    control={<Switch checked={Boolean(fiche?.is_active)}
                                     onChange={poser('is_active')} />}
                    label="Proposée dans le chat"
                  />
                </Stack>

                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Ce que la recette a le droit de créer
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                              component="div" sx={{ mb: 1 }}>
                    Rien de coché : tout lui est permis. L'expert confirme de
                    toute façon chaque création.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {outils.map((outil) => (
                      <FormControlLabel
                        key={outil.name}
                        control={(
                          <Checkbox size="small"
                                    checked={Boolean(
                                      fiche?.allowed_tools?.includes(outil.name))}
                                    onChange={() => basculerOutil(outil.name)} />
                        )}
                        label={outil.label}
                      />
                    ))}
                  </Box>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setFiche(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || !fiche?.name?.trim()
                                || !fiche?.prompt?.trim()}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer cette recette ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.name} » ne sera plus proposée dans le chat.
              Pour la retirer sans la perdre, décochez plutôt « Proposée dans
              le chat ».
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default AssistantSkills;
